import AnimalTypes from "@wesstron/utils/Api/constants/animalTypes";
import {get} from "lodash";
import React, {useCallback, useEffect, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {SelectedItemStatus} from "../../../../../constans/animalDocuments";
import {
    PARTURITION_DIFFICULTY,
    USG_STATE,
} from "../../../../../constans/eventTypes";
import {FilterTypes} from "../../../../../constans/filter";
import {SectorType} from "../../../../../constans/sectorTypes";
import sowState from "../../../../../constans/sowState";
import animalsDB from "../../../../../database/animalsDB";
import buildingsDB from "../../../../../database/buildingsDB";
import {
    getFallReasons,
    getRaces,
    getSelectionReasons,
    getSellReasons,
} from "../../../../../selectors/dictionarySelectors";
import {getEmployeeOptionList} from "../../../../../selectors/userSelector";
import {formatAnimalName} from "../../../../../utils/AnimalsUtils";
import {REMOVE_TECHNOLOGY_GROUP_REASONS} from "../../../../../utils/TechnologyGroupUtils";
import Button from "../../../button/Button";
import Input from "../../../input/Input";
import Select from "../../../select/Select";
import TreeSelect from "../../../tree-select/TreeSelect";
import moment from "moment";
import {filterMap} from "../../../../../utils/Utils";
import {removeReasonCodeToTranslationKey} from "../../../../../views/new-farm-view/tabs/results-renderers/ResultsReportFormatters";

export default function FilterRow({
    value,
    headers: headersProp,
    onFilterChange,
    path,
    index,
    onRemove,
    farm,
    technologyGroupFilterAsString = false, // uzyj jezeli uzywasz w sqlu, bo inaczej jest konflikt VARCHAR - INTEGER
}) {
    const {t} = useTranslation();

    const fallReasons = useSelector(getFallReasons);
    const races = useSelector(getRaces);
    const employees = useSelector(getEmployeeOptionList);
    const sellReasons = useSelector(getSellReasons);
    const selectionReasons = useSelector(getSelectionReasons);

    const keys = useMemo(() => Object.keys(value), [value]);

    const column = useMemo(() => keys[0] || null, [keys]);

    const valueKeys = useMemo(
        () => Object.keys(value[column] || {}),
        [column, value]
    );

    const type = useMemo(() => valueKeys[0] || null, [valueKeys]);

    const inputValue = useMemo(() => {
        const val = value?.[column]?.[type];
        if (val !== undefined) return val;
        return null;
    }, [column, type, value]);

    const treeValues = useMemo(
        () => buildingsDB.getObjectForTreeSelect(farm),
        [farm]
    );

    const headers = useMemo(
        () =>
            headersProp
                .filter((item) => item.filterType)
                .map((val) => ({
                    name: val.name,
                    value: val.filterColumn || val.field,
                })),
        [headersProp]
    );

    const header = useMemo(
        () =>
            headersProp.find(
                (item) => (item.filterColumn || item.field) === column
            ),
        [column, headersProp]
    );

    const inputType = useMemo(() => {
        switch (header?.filterType) {
            case FilterTypes.NUMBER:
                return "number";
            case FilterTypes.DATE:
                if (type === "$lastDays") return "number";
                return "date";
            default:
                return "text";
        }
    }, [header, type]);

    const options = useMemo(() => {
        switch (header?.filterType) {
            case FilterTypes.USG_RESULT:
                return [
                    {
                        name: t("events.usgEvent.negative"),
                        value: USG_STATE.NEGATIVE,
                    },
                    {
                        name: t("events.usgEvent.positive"),
                        value: USG_STATE.POSITIVE,
                    },
                    {
                        name: t("events.usgEvent.toRepeat"),
                        value: USG_STATE.REPEAT,
                    },
                ];
            case FilterTypes.RESULT_STATUS:
                return [
                    {
                        name: t("OK"),
                        value: SelectedItemStatus.OK,
                    },
                    {
                        name: t("inseminationEffectivenes.noInsemination"),
                        value: SelectedItemStatus.NO_INSEMINATION,
                    },
                ];
            case FilterTypes.BOOLEAN:
                return [
                    {
                        name: t("yes"),
                        value: true,
                    },
                    {
                        name: t("no"),
                        value: false,
                    },
                ];
            case FilterTypes.PARTURITION_LEVEL:
                return [
                    {
                        name: t("low"),
                        value: PARTURITION_DIFFICULTY.LOW,
                    },
                    {
                        name: t("medium"),
                        value: PARTURITION_DIFFICULTY.MEDIUM,
                    },
                    {
                        name: t("hard"),
                        value: PARTURITION_DIFFICULTY.HARD,
                    },
                ];
            case FilterTypes.FALL_REASON:
                return fallReasons.WData.map((row) => ({
                    name: row.Value,
                    value: row.ID,
                }));
            case FilterTypes.RACE:
                return races.WData.map((row) => ({
                    name: row.Value,
                    value: row.ID,
                }));
            case FilterTypes.TECHNOLOGY_GROUP_REMOVAL:
                const createValue = (val) => {
                    if (technologyGroupFilterAsString) return val + "";
                    return val;
                };
                return filterMap(
                    Object.values(REMOVE_TECHNOLOGY_GROUP_REASONS),
                    (code) => {
                        const translationKey =
                            removeReasonCodeToTranslationKey(code);
                        if (!translationKey) return null;
                        return {
                            value: createValue(code),
                            name: t(translationKey),
                        };
                    }
                );
            case FilterTypes.SECTOR_TYPES:
                return Object.values(SectorType).map((val) => ({
                    name: t(`SType.${val - 1}`),
                    value: val,
                }));
            case FilterTypes.BOARS:
                const boars = animalsDB.getAllAnimals(
                    farm,
                    AnimalTypes.BOAR,
                    true,
                    false
                );
                return boars
                    .map((animal) => ({
                        name: formatAnimalName(animal),
                        value: animal.AnmID,
                    }))
                    .sort((a, b) =>
                        a.name.localeCompare(b.name, undefined, {numeric: true})
                    );
            case FilterTypes.SOW_STATE:
                return [
                    {
                        name: t("sowIdle"),
                        value: sowState.IDLE,
                    },
                    {
                        name: t("inseminated"),
                        value: sowState.INSEMINATED,
                    },
                    {
                        name: t("inLactation"),
                        value: sowState.FARROWED,
                    },
                    {
                        name: t("separated"),
                        value: sowState.SEPARATED,
                    },
                    {
                        name: t("notInProduction"),
                        value: sowState.NOT_IN_PRODUCTION,
                    },
                ];
            case FilterTypes.USER:
                return employees;
            case FilterTypes.WEEK_DAY: {
                const tmp = [];
                for (let i = 1; i <= 7; i++) {
                    const date = moment().isoWeekday(i);
                    tmp.push({
                        name: date.format("dddd"),
                        value: i,
                    });
                }
                return tmp;
            }
            case FilterTypes.MONTH: {
                const tmp = [];
                for (let i = 0; i < 12; i++) {
                    const date = moment().month(i);
                    tmp.push({
                        name: date.format("MMMM"),
                        value: i,
                    });
                }
                return tmp;
            }
            case FilterTypes.ANIMAL_KIND: {
                return [
                    {
                        name: t("animalKind.5"),
                        value: AnimalTypes.RENOVATION_SOW,
                    },
                    {name: t("animalKind.0"), value: AnimalTypes.SOW},
                    {name: t("animalKind.2"), value: AnimalTypes.PIGLET},
                    {name: t("animalKind.3"), value: AnimalTypes.PORKER},
                ];
            }
            case FilterTypes.SELL_REASON: {
                return sellReasons.map((row) => ({
                    name: row.Value,
                    value: row.ID,
                }));
            }
            case FilterTypes.SELECTION_REASON: {
                return (selectionReasons?.WData || []).map((row) => ({
                    name: row.Value,
                    value: row.ID,
                }));
            }
            default:
                return [];
        }
    }, [
        header,
        t,
        fallReasons,
        farm,
        races,
        employees,
        technologyGroupFilterAsString,
        sellReasons,
        selectionReasons,
    ]);

    const shouldRenderSelect = useMemo(() => {
        return [
            FilterTypes.LOCATION,
            FilterTypes.USG_RESULT,
            FilterTypes.RESULT_STATUS,
            FilterTypes.BOOLEAN,
            FilterTypes.PARTURITION_LEVEL,
            FilterTypes.FALL_REASON,
            FilterTypes.TECHNOLOGY_GROUP_REMOVAL,
            FilterTypes.SECTOR_TYPES,
            FilterTypes.BOARS,
            FilterTypes.SOW_STATE,
            FilterTypes.RACE,
            FilterTypes.WEEK_DAY,
            FilterTypes.USER,
            FilterTypes.MONTH,
            FilterTypes.ANIMAL_KIND,
            FilterTypes.SELL_REASON,
            FilterTypes.SELECTION_REASON,
        ].includes(header?.filterType);
    }, [header]);

    const shouldRenderTreeSelect = useMemo(() => {
        return header?.filterType === FilterTypes.LOCATION;
    }, [header]);

    const onColumnChange = useCallback(
        (column) => {
            let pathWithIndex = path + `[${index}]`;
            const val = JSON.parse(JSON.stringify(value));
            let currentValue = get(val, keys[0], null);
            delete val[keys[0]];
            if (column !== null) {
                val[column] = currentValue;
            }
            onFilterChange(val, pathWithIndex);
        },
        [index, onFilterChange, path, value, keys]
    );

    const typeOptions = useMemo(() => {
        switch (header?.filterType) {
            case FilterTypes.DEV_ADDRESS: {
                return [
                    {
                        name: t("equals"),
                        value: "$devAdrEq",
                    },
                ];
            }
            case FilterTypes.NUMBER:
                return [
                    {
                        name: t("filterGrid.greaterThanOrEquals"),
                        value: "$sanGte",
                    },
                    {
                        name: t("filterGrid.lessThanOrEquals"),
                        value: "$sanLte",
                    },
                    {
                        name: t("equals"),
                        value: "$sanEq",
                    },
                    {
                        name: t("filterGrid.notEquals"),
                        value: "$sanNe",
                    },
                ];
            case FilterTypes.STRING:
                return [
                    {
                        name: t("contains"),
                        value: "$includes",
                    },
                    {
                        name: t("equals"),
                        value: "$eq",
                    },
                    {
                        name: t("filterGrid.startsWith"),
                        value: "$startsWith",
                    },
                    {
                        name: t("endWith"),
                        value: "$endsWith",
                    },
                    {
                        name: t("filterGrid.notEquals"),
                        value: "$ne",
                    },
                ];
            case FilterTypes.DATE:
                return [
                    {
                        name: t("equals"),
                        value: "$dateEq",
                    },
                    {
                        name: t("filterGrid.greaterThanOrEquals"),
                        value: "$dateGte",
                    },
                    {
                        name: t("filterGrid.lessThanOrEquals"),
                        value: "$dateLte",
                    },
                    {
                        name: t("today"),
                        value: "$today",
                    },
                    {
                        name: t("yesterday"),
                        value: "$yesterday",
                    },
                    {
                        name: t("thisWeek"),
                        value: "$thisWeek",
                    },
                    {
                        name: t("lastWeek"),
                        value: "$lastWeek",
                    },
                    {
                        name: t("thisMonth"),
                        value: "$thisMonth",
                    },
                    {
                        name: t("lastMonth"),
                        value: "$lastMonth",
                    },
                    {
                        name: t("thisYear"),
                        value: "$thisYear",
                    },
                    {
                        name: t("lastYear"),
                        value: "$lastYear",
                    },
                    {
                        name: t("lastXDays"),
                        value: "$lastDays",
                    },
                ];
            case FilterTypes.TECHNOLOGY_GROUP:
                return [
                    {
                        name: t("contains"),
                        value: "$includes",
                    },
                    {
                        name: t("equals"),
                        value: "$eq",
                    },
                    {
                        name: t("filterGrid.startsWith"),
                        value: "$startsWith",
                    },
                    {
                        name: t("endWith"),
                        value: "$endsWith",
                    },
                    {
                        name: t("inseminationXWeeksAgo"),
                        value: "$techGroupAge",
                    },
                    {
                        name: t("filterGrid.notEquals"),
                        value: "$ne",
                    },
                ];
            case FilterTypes.LOCATION:
                return [
                    {
                        name: t("filterGrid.below"),
                        value: "$locBelow",
                    },
                    {
                        name: t("filterGrid.onlyLevel"),
                        value: "$locLevel",
                    },
                ];
            case FilterTypes.CYCLE:
                return [
                    {
                        name: t("filterGrid.greaterThanOrEquals"),
                        value: "$sanGte",
                    },
                    {
                        name: t("filterGrid.lessThanOrEquals"),
                        value: "$sanLte",
                    },
                    {
                        name: t("equals"),
                        value: "$sanEq",
                    },
                    {
                        name: t("filterGrid.notEquals"),
                        value: "$sanNe",
                    },
                    {
                        name: t("lastAnimalCycles"),
                        value: "$lastCycle",
                    },
                ];
            default:
                return [
                    {
                        name: t("equals"),
                        value: "$sanEq",
                    },
                    {
                        name: t("filterGrid.notEquals"),
                        value: "$sanNe",
                    },
                ];
        }
    }, [t, header]);

    const onTypeChange = useCallback(
        (type) => {
            let pathWithIndex = path + `[${index}]`;
            const val = JSON.parse(JSON.stringify(value));
            if (val[column]) {
                let keysFromColumn = Object.keys(val[column]);
                let currentValue = val[column][keysFromColumn[0]];
                val[column] = {[type]: currentValue};
            } else {
                val[column] = {[type]: null};
            }
            onFilterChange(val, pathWithIndex);
        },
        [index, onFilterChange, path, value, column]
    );

    const onRemoveClick = useCallback(() => {
        onRemove(path, index);
    }, [onRemove, path, index]);

    const onInputChange = useCallback(
        (val) => {
            let pathWithIndex = path + `[${index}]`;
            let tmp = JSON.parse(JSON.stringify(value));
            if (inputType === "number") {
                if (val) val = +val;
                else val = null;
            }
            tmp[column][type] = val;
            onFilterChange(tmp, pathWithIndex);
        },
        [column, index, onFilterChange, path, type, value, inputType]
    );

    const canRenderValue = useMemo(() => {
        return ![
            "$today",
            "$yesterday",
            "$thisWeek",
            "$lastWeek",
            "$thisMonth",
            "$lastMonth",
            "$thisYear",
            "$lastYear",
        ].includes(type);
    }, [type]);

    useEffect(() => {
        if (!canRenderValue) {
            onInputChange("");
        }
    }, [canRenderValue]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className={`filter-row${!canRenderValue ? " no-value" : ""}`}>
            <Select
                value={column}
                options={headers}
                onChange={onColumnChange}
                placeholder={t("filterGrid.column")}
            />
            <Select
                value={type}
                options={typeOptions}
                onChange={onTypeChange}
                disabled={!column}
                placeholder={t("filterGrid.condition")}
            />
            {canRenderValue && (
                <>
                    {shouldRenderTreeSelect && (
                        <TreeSelect
                            options={treeValues}
                            value={inputValue}
                            onChange={onInputChange}
                            disabled={!type}
                            expanded
                            placeholder={t("value")}
                        />
                    )}
                    {shouldRenderSelect && !shouldRenderTreeSelect && (
                        <Select
                            value={inputValue}
                            options={options}
                            onChange={onInputChange}
                            disabled={!type}
                            placeholder={t("value")}
                        />
                    )}
                    {!shouldRenderSelect && !shouldRenderTreeSelect && (
                        <Input
                            type={inputType}
                            disabled={!type}
                            onChange={onInputChange}
                            value={inputValue ?? ""}
                            placeholder={t("value")}
                        />
                    )}
                </>
            )}
            <Button
                type="button"
                icon={<i className="fas fa-trash" />}
                onClick={onRemoveClick}
            />
        </div>
    );
}
